import { USE_MOCKS, api, mockDelay } from "@/lib/api"
import type {
  CashflowRow,
  ConciliacionRow,
  ConsiderarEn,
  CountryRow,
  FacturasRow,
  FuentePnl,
  KpisRow,
  OrigenPnl,
  PnlRow,
  ContrasteRow,
  FuenteFinanzas,
  Frontera,
  PuenteRow,
  RuteoRow,
  EdicionCelda,
  EdicionRow,
  RatiosRow,
  CajaBancosRow,
} from "@/routes/finance/mapping"

/**
 * Modulo `finance` del backend: la vista de CFO.
 *
 *   GET /api/finance/kpis?anio            cifras de portada + variacion interanual
 *   GET /api/finance/pnl?anio&pais        P&L mensual con subtotales
 *   GET /api/finance/pnl/by-country?anio  ingresos y EBITDA por pais
 *   GET /api/finance/cashflow?anio&pais   caja mensual
 *
 * `pais` vacio = CONSOLIDADO, que el backend SUMA en vez de leer de una fila
 * guardada. Todo viene en USD: el modelo financiero de Siete consolida cuatro
 * paises y tres monedas a dolar con el tipo de cambio de cada mes.
 */

const vacio: KpisRow["actual"] = {
  ingresos: 0, gasto_operativo: 0, costo_equipo: 0,
  ebitda: 0, ebitda_pct: null, ingresos_por_linea: {},
}

/**
 * Las cuatro cifras de portada.
 *
 * `pais` NO ESTABA, y la tabla de debajo sí lo mandaba: con México elegido, las
 * tarjetas seguian siendo del grupo y la tabla era de Mexico. La pantalla se
 * veia perfecta y sus dos mitades hablaban de empresas distintas.
 */
export async function fetchKpis(anio: number, pais?: string): Promise<KpisRow> {
  if (USE_MOCKS) {
    return mockDelay<KpisRow>({
      anio, pais: pais ?? null, moneda: "USD",
      actual: vacio, anio_anterior: vacio, variacion: {},
    })
  }
  return api.get<KpisRow>("/finance/kpis", { query: { anio, pais } })
}

export async function fetchPnl(
  anio: number, pais?: string, origen?: OrigenPnl, fuente?: FuentePnl,
): Promise<PnlRow> {
  if (USE_MOCKS) {
    return mockDelay<PnlRow>({
      anio, pais: pais ?? "CONSOLIDADO", origen: origen ?? "MOCK", moneda: "USD",
      fuente: fuente ?? "hoja", meses_declarados: [],
      cuentas: [],
      subtotales: { margen_bruto: { meses: {}, total: 0 }, ebitda: { meses: {}, total: 0 } },
      indicadores: { ingresos: 0, margen_bruto_pct: null, ebitda_pct: null },
    })
  }
  return api.get<PnlRow>("/finance/pnl", {
    query: { anio, pais: pais || undefined, origen, fuente },
  })
}

export async function fetchPnlByCountry(
  anio: number, origen?: OrigenPnl,
): Promise<CountryRow[]> {
  if (USE_MOCKS) return mockDelay<CountryRow[]>([])
  return api.get<CountryRow[]>("/finance/pnl/by-country", { query: { anio, origen } })
}

/* ---- La conciliación bancaria (migración 0036) ---- */

export interface SugerenciaDeConciliacion {
  tipo: "factura" | "comprobante"
  id: string
  etiqueta: string
  importe: number
  fecha: string | null
  diferencia: number
  dias: number
  nombra: boolean
  confianza: "exacta" | "probable"
  por_que: string
}

export interface MovimientoConciliable {
  id: string
  fecha: string
  importe: number
  moneda: string
  descripcion: string | null
  referencia: string | null
  contraparte: string | null
  categoria: string | null
  cuenta: string
  pais: string | null
  estado: "conciliado" | "sugerido" | "sin_conciliar"
  conciliado: { tipo: string; id: string | null; etiqueta: string; en: string | null; motivo: string | null; automatica: boolean } | null
  sugerencias: SugerenciaDeConciliacion[]
}

export interface ConciliacionBancaria {
  anio: number
  mes: number
  pais: string
  movimientos: MovimientoConciliable[]
  resumen: { movimientos: number; conciliados: number; sugeridos: number; sin_conciliar: number }
  nota: string
}

export function fetchConciliacionBancaria(anio: number, mes: number, pais?: string): Promise<ConciliacionBancaria> {
  return api.get<ConciliacionBancaria>("/finance/conciliacion-bancaria", { query: { anio, mes, pais: pais || undefined } })
}

export function conciliarMovimiento(id: string, tipo: "factura" | "comprobante" | "manual", refId: string | null, motivo: string) {
  return api.post(`/finance/conciliacion-bancaria/${id}`, { tipo, ref_id: refId, motivo })
}

export function desconciliarMovimiento(id: string) {
  return api.delete(`/finance/conciliacion-bancaria/${id}`)
}

/* ---- El P&L por área, desde el libro gerencial ---- */

export interface PnlPorArea {
  anio: number
  pais: string
  moneda: string
  areas: Array<{
    codigo: string | null
    nombre: string
    ingreso: PorMes; costo: PorMes; gasto: PorMes; resultado: PorMes
    total_ingreso: number; total_costo: number; total_gasto: number; total_resultado: number
  }>
  sin_cambio: number
  nota: string
}

export function fetchPnlPorArea(anio: number, pais?: string): Promise<PnlPorArea> {
  return api.get<PnlPorArea>("/finance/pnl/por-area", { query: { anio, pais: pais || undefined } })
}

export interface GastosPorProveedor {
  anio: number
  pais: string
  tipo: "gasto" | "costo"
  moneda: string
  proveedores: Array<{ proveedor: string; tax_id: string; meses: PorMes; total: number; documentos: number; grupos: string[] }>
  sin_clasificar: { meses: PorMes; total: number; documentos: number }
  sin_cotizacion: number
  nota: string
}

export function fetchGastosPorProveedor(anio: number, tipo: "gasto" | "costo", pais?: string): Promise<GastosPorProveedor> {
  return api.get<GastosPorProveedor>("/finance/gastos/por-proveedor", { query: { anio, tipo, pais: pais || undefined } })
}

/**
 * LA SALUD DEL CIERRE: cuánto de lo comprobable está en orden. Ver `salud.py`.
 * Es un recuento de hechos con su siguiente paso, no una medalla.
 */
export interface LogroDeSalud {
  clave: string
  titulo: string
  logrado: boolean
  /** 0–1: cuánto de la señal está bien. */
  credito: number
  peso: number
  detalle: string
  /** Ruta donde se arregla. */
  a: string
}

export interface SaludDelCierre {
  puntos: number
  nivel: "al_dia" | "casi" | "con_pendientes"
  racha_meses: number
  logros: LogroDeSalud[]
  logrados: number
  siguiente: string | null
  nota: string
}

export function fetchSalud(): Promise<SaludDelCierre> {
  return api.get<SaludDelCierre>("/finance/salud")
}

export function fetchCajaBancos(anio: number, pais?: string): Promise<CajaBancosRow> {
  return api.get<CajaBancosRow>("/finance/caja-bancos", { query: { anio, pais: pais || undefined } })
}

/** Pisar una celda futura de la caja por categoría. Ver `armado_caja.py`. */
export function pisarCajaForecast(body: {
  pais?: string; anio: number; mes: number; categoria: string
  valor_usd: number; revierte_a?: number | null; motivo: string
}): Promise<{ mensaje: string }> {
  return api.put<{ mensaje: string }>("/finance/caja-bancos/forecast", { ...body, pais: body.pais || null })
}

export function quitarCajaForecast(params: { pais?: string; anio: number; mes: number; categoria: string }): Promise<{ mensaje: string }> {
  return api.delete<{ mensaje: string }>("/finance/caja-bancos/forecast", {
    query: { ...params, pais: params.pais || undefined },
  })
}

/* ------------------------------------------------------------------------ */
/* El armado: los movimientos bancarios, en el maestro                        */
/* ------------------------------------------------------------------------ */

export interface MovimientoBancario {
  id: string
  fecha: string
  importe: number
  moneda: string
  importe_usd: number | null
  descripcion: string | null
  referencia: string | null
  contraparte: string | null
  categoria: string | null
  nombre_categoria: string | null
  /** Quién la puso: el archivo, una regla aprendida, o una persona. */
  categoria_por: "archivo" | "regla" | "persona" | null
  considerar: boolean
  nota: string | null
  conciliado: boolean
  /** A qué factura responde: venta (factura emitida), gasto (comprobante), a mano, sugerida o no reconocida. */
  factura: {
    estado: "venta" | "gasto" | "a_mano" | "sugerida" | "no_reconocida"
    etiqueta: string | null
    automatica: boolean
    sugerencia: { id: string; tipo: "factura" | "comprobante"; etiqueta: string; confianza: "exacta" | "probable"; por_que: string } | null
  }
  cuenta_id: string
  cuenta: string
  pais: string | null
}

export interface ReglaDeCategoria {
  id: string
  patron: string
  categoria: string
  nombre_categoria: string
  veces_aplicada: number
  creado_en: string
  creado_por: string | null
}

export interface MovimientosBancarios {
  anio: number
  mes: number | null
  movimientos: MovimientoBancario[]
  resumen: {
    movimientos: number; sin_categoria: number; no_considerados: number
    con_nota: number; por_regla: number; por_persona: number
    no_reconocidas: number; sugeridas: number
  }
  categorias: Array<{ id: string; nombre: string }>
  reglas: ReglaDeCategoria[]
}

export type FiltroDeMovimientos = "sin_categoria" | "no_considerados" | "con_nota" | "no_reconocidas"

export function fetchMovimientosBancarios(params: {
  anio: number; mes?: number | null; cuenta_id?: string | null; solo?: FiltroDeMovimientos | null
}): Promise<MovimientosBancarios> {
  return api.get<MovimientosBancarios>("/finance/movimientos-bancarios", {
    query: { anio: params.anio, mes: params.mes || undefined, cuenta_id: params.cuenta_id || undefined,
             solo: params.solo || undefined },
  })
}

export function categorizarMovimientos(body: { ids: string[]; categoria: string; recordar: boolean }):
  Promise<{ categorizados: number; reglas: number; por_regla: number }> {
  return api.patch("/finance/movimientos-bancarios/categoria", body)
}

export function anotarMovimiento(id: string, body: { considerar?: boolean; nota?: string }):
  Promise<{ considerar: boolean; nota: string | null }> {
  return api.patch(`/finance/movimientos-bancarios/${id}`, body)
}

export function borrarReglaDeCategoria(id: string): Promise<{ patron: string; categoria: string }> {
  return api.delete(`/finance/movimientos-bancarios/reglas/${id}`)
}

export async function fetchCashflow(anio: number, pais?: string): Promise<CashflowRow> {
  if (USE_MOCKS) {
    return mockDelay<CashflowRow>({
      anio, pais: pais ?? "CONSOLIDADO", moneda: "USD",
      entradas: {}, salidas: {}, neto: {}, acumulado: {}, cuentas: [],
      saldo_inicial: 0, acumula_desde: null, saldo_inicial_completo: false,
      caja_bancos: null,
    })
  }
  return api.get<CashflowRow>("/finance/cashflow", { query: { anio, pais: pais || undefined } })
}

/**
 * Ratios. Se calculan en el BACKEND, no aca.
 *
 * Un margen calculado en dos sitios da dos numeros distintos el dia que alguien
 * toca uno, y el que se vea primero sera el que se use para decidir. La pantalla
 * solo los formatea.
 *
 * `mes_desde`/`mes_hasta` hacen que el periodo elegido en el dashboard llegue
 * hasta el calculo: sin eso el filtro seria un rotulo.
 */
export async function fetchRatios(
  anio: number, mesDesde: number, mesHasta: number, pais?: string,
  comparar: string = "interanual",
): Promise<RatiosRow> {
  if (USE_MOCKS) {
    return mockDelay<RatiosRow>({
      periodo: { anio, mes_desde: mesDesde, mes_hasta: mesHasta, meses: 12 },
      pais: pais ?? "CONSOLIDADO", moneda: "USD",
      comparacion: {
        tipo: comparar, etiqueta: "",
        ventana: { desde: { anio, mes: mesDesde }, hasta: { anio, mes: mesHasta } },
      },
      base: {}, ratios: [], no_disponibles: [],
    })
  }
  return api.get<RatiosRow>("/finance/ratios", {
    query: { anio, mes_desde: mesDesde, mes_hasta: mesHasta,
             pais: pais || undefined, comparar },
  })
}

/* -------------------------------------------------------------- edicion --- */

/**
 * Corrige un importe del modelo.
 *
 * NO sobrescribe la hoja: el backend guarda la correccion como una fila aparte
 * que gana en la lectura. Reimportar el Google Sheet no la pisa, se puede ver
 * quien la hizo y se puede deshacer.
 */
export async function editarCelda(edicion: EdicionCelda) {
  if (USE_MOCKS) return mockDelay({ mensaje: "mock" })
  return api.patch<{ mensaje: string; de_la_hoja: number | null }>(
    "/finance/facts", edicion)
}

/** Quita la correccion y devuelve el mando a la hoja. */
export async function revertirCelda(c: {
  pais: string; bloque: string; sub_account: string
  anio: number; mes: number; motivo: string
}) {
  if (USE_MOCKS) return mockDelay({ mensaje: "mock" })
  return api.delete<{ mensaje: string; importe_usd: number | null }>(
    "/finance/facts", { query: { ...c } })
}

/** Todo lo que una persona cambio, con su delta contra la hoja. */
export async function fetchEdiciones(anio?: number): Promise<EdicionRow[]> {
  if (USE_MOCKS) return mockDelay<EdicionRow[]>([])
  return api.get<EdicionRow[]>("/finance/edits", { query: { anio } })
}

/* --------------------------------------------------------- conciliacion --- */

/**
 * Facturado contra ingreso reconocido, por pais y mes.
 *
 *   GET /api/finance/reconciliation?anio
 *   GET /api/finance/invoices?anio&mes&pais&considerar_en&estado&buscar
 *   PATCH /api/finance/invoices/{id}
 *
 * El corte es POR PAIS y no un total consolidado a proposito: "no cuadra" y "no
 * esta cargado" piden acciones opuestas —una se investiga, la otra se importa—
 * y un consolidado las mezcla hasta volverlas indistinguibles.
 */
export async function fetchConciliacion(anio: number): Promise<ConciliacionRow> {
  if (USE_MOCKS) {
    return mockDelay<ConciliacionRow>({ anio, moneda: "USD", nota: "", paises: [] })
  }
  return api.get<ConciliacionRow>("/finance/reconciliation", { query: { anio } })
}

export async function fetchFacturas(filtros: {
  anio?: number
  mes?: number
  pais?: string
  considerar_en?: string
  estado?: string
  buscar?: string
}): Promise<FacturasRow> {
  if (USE_MOCKS) {
    return mockDelay<FacturasRow>({ facturas: [], hay_mas: false, limite: 0, nota: "" })
  }
  return api.get<FacturasRow>("/finance/invoices", {
    query: {
      anio: filtros.anio,
      mes: filtros.mes || undefined,
      pais: filtros.pais || undefined,
      considerar_en: filtros.considerar_en || undefined,
      estado: filtros.estado || undefined,
      buscar: filtros.buscar?.trim() || undefined,
    },
  })
}

/**
 * Decide en que lado cuenta una factura.
 *
 * Es lo unico editable de una factura importada, y a proposito: el importe y el
 * numero son lo que la sociedad emitio. El motivo es obligatorio para excluir y
 * se ignora al volver a "ambos", que es donde no hay nada que justificar.
 */
export async function considerarFactura(
  id: string, considerar_en: ConsiderarEn, motivo?: string,
) {
  if (USE_MOCKS) return mockDelay({ mensaje: "mock" })
  return api.patch<{ mensaje: string; considerar_en: ConsiderarEn; motivo_exclusion: string | null }>(
    `/finance/invoices/${encodeURIComponent(id)}`, { considerar_en, motivo })
}

/* ==========================================================================
 * EL FORECAST DESDE LO DECLARADO
 * ==========================================================================
 * Otra fuente, no otra vista: el resto de este archivo lee la hoja de gestión
 * importada, y esto lee contratos, planilla y lo que se declaró. Ver
 * `docs/forecast-desde-maestros.md`.
 */

/** Un mes -> importe. Las claves llegan como cadenas por JSON. */
export type PorMes = Record<string, number>

export interface BaseDeclarada {
  anio: number
  pais: string | null
  ingreso: {
    firmado: PorMes
    apostado: PorMes
    declarado: PorMes
    total: PorMes
    /** `null` cuando no hay ingreso ese mes: 0% se leería como «nada firmado». */
    cobertura_firmada: Record<string, number | null>
  }
  costo: { total: PorMes }
  fuentes: {
    contratos: {
      contratado: PorMes
      renovacion: PorMes
      clientes: Array<{
        cliente: string
        pais: string
        linea: string | null
        meses: PorMes
        meses_renovacion: PorMes
      }>
      no_convertible: number
    }
    hitos: {
      por_mes: PorMes
      detalle: Array<{
        cliente: string
        descripcion: string
        estado: string
        fecha_prevista: string
        mes: number
        importe_usd: number
      }>
    }
    producto: {
      ingreso: PorMes
      costo: PorMes
      unidades: PorMes
      detalle: Array<{
        producto: string
        sku: string | null
        pais: string
        unidades_mes: number
        precio_unitario_usd: number
        costo_unitario_usd: number | null
        margen_unitario_usd: number | null
        crecimiento_mensual_pct: number
      }>
    }
    oportunidades: {
      por_mes: PorMes
      detalle: Array<{
        cliente: string
        linea: string | null
        probabilidad: number
        estado: string
        monto_mensual_usd: number
        ponderado_mensual_usd: number
      }>
    }
    planilla: { facturable: PorMes; estructura: PorMes; personas: Record<string, number> }
    plan_personas: {
      contrataciones_facturable: PorMes
      contrataciones_estructura: PorMes
      cabezas_nuevas: Record<string, number>
      aumentos: PorMes
      detalle: Array<{
        tipo: string
        puesto?: string | null
        cantidad?: number
        costo_mensual_usd?: number
        es_facturable?: boolean
        porcentaje?: number
        meses: number[]
      }>
    }
    crecimiento: {
      por_cuenta: Record<string, PorMes>
      detalle: Array<{
        categoria: string
        concepto: string
        cuenta: string
        importe_mensual_usd: number
        meses: number[]
      }>
    }
  }
  /** El supuesto de tipo de cambio, en palabras. Se enseña con las cifras. */
  supuestos_fx: string | null
}

export async function fetchBaseDeclarada(
  anio: number,
  pais?: string,
): Promise<BaseDeclarada> {
  return api.get<BaseDeclarada>("/finance/forecast/declarado", {
    query: { anio, pais },
  })
}

export interface ContraHoja {
  anio: number
  ingreso: { hoja: PorMes; declarado: PorMes; firmado: PorMes; cobertura_pct: Record<string, number | null> }
  planilla: { hoja: PorMes; declarado: PorMes; cobertura_pct: Record<string, number | null> }
  nota: string
}

export async function fetchContraHoja(anio: number, pais?: string): Promise<ContraHoja> {
  return api.get<ContraHoja>("/finance/forecast/declarado/contra-hoja", {
    query: { anio, pais },
  })
}

/* --------------------------------------------------------------------------
 * DAR DE ALTA LO QUE SE DECLARA
 * --------------------------------------------------------------------------
 * Faltaba esto y era lo que dejaba la pantalla a medias: los doce endpoints
 * existían y la siembra llenaba los paneles, así que «De dónde sale» se podía
 * leer y no se podía ALIMENTAR. Una pantalla que muestra lo que declaraste y no
 * deja declarar nada obliga a volver a la consola para cada cambio, que es la
 * versión cara del punto 7 de Cesar: «debería de poder agregar nuevos clientes
 * a ese revenue build up».
 *
 * Las cinco altas van contra los mismos endpoints que usa la siembra, a
 * propósito: si la pantalla escribiera por otra vía, lo sembrado y lo escrito a
 * mano podrían diferir en las validaciones y nadie lo vería hasta que el
 * forecast saliera raro.
 */

export interface OportunidadIn {
  entity_id: string
  cliente_nombre: string
  linea_servicio?: string | null
  monto_mensual_usd: number
  anio: number
  desde_mes: number
  meses?: number | null
  probabilidad: number
  estado?: string
  nota?: string | null
}

export interface ProductoIn {
  entity_id: string
  producto: string
  sku?: string | null
  linea_servicio?: string | null
  anio: number
  desde_mes: number
  hasta_mes?: number | null
  unidades_mes: number
  precio_unitario_usd: number
  costo_unitario_usd?: number | null
  crecimiento_mensual_pct: number
  nota?: string | null
}

export interface CrecimientoIn {
  pais: string
  anio: number
  desde_mes: number
  hasta_mes?: number | null
  categoria: string
  concepto: string
  importe_mensual_usd: number
  nota?: string | null
}

export interface PlanPersonasIn {
  entity_id: string
  tipo: "contratacion" | "aumento"
  anio: number
  desde_mes: number
  hasta_mes?: number | null
  puesto?: string | null
  cantidad?: number | null
  costo_mensual_usd?: number | null
  es_facturable?: boolean | null
  porcentaje?: number | null
  nota?: string | null
}

export interface HitoIn {
  contract_line_id: string
  descripcion: string
  fecha_prevista: string
  monto: number
  estado?: string
  nota?: string | null
}

export const crearOportunidad = (b: OportunidadIn) =>
  api.post<{ id: string }>("/finance/forecast/oportunidades", b)
export const borrarOportunidad = (id: string) =>
  api.delete<unknown>(`/finance/forecast/oportunidades/${encodeURIComponent(id)}`)

export const crearProducto = (b: ProductoIn) =>
  api.post<{ id: string }>("/finance/forecast/productos", b)
export const borrarProducto = (id: string) =>
  api.delete<unknown>(`/finance/forecast/productos/${encodeURIComponent(id)}`)

export const crearCrecimiento = (b: CrecimientoIn) =>
  api.post<{ id: string }>("/finance/forecast/crecimiento", b)
export const borrarCrecimiento = (id: string) =>
  api.delete<unknown>(`/finance/forecast/crecimiento/${encodeURIComponent(id)}`)

export const crearPlanPersonas = (b: PlanPersonasIn) =>
  api.post<{ id: string }>("/finance/forecast/plan-personas", b)
export const borrarPlanPersonas = (id: string) =>
  api.delete<unknown>(`/finance/forecast/plan-personas/${encodeURIComponent(id)}`)

export const crearHito = (b: HitoIn) =>
  api.post<{ id: string }>("/finance/forecast/hitos", b)
export const borrarHito = (id: string) =>
  api.delete<unknown>(`/finance/forecast/hitos/${encodeURIComponent(id)}`)

/**
 * Cargar facturación a mano, una factura a la vez.
 *
 * `POST /finance/invoices` recibe una LISTA, porque el mismo endpoint sirve a la
 * carga por archivo. Desde el traspaso se manda de una en una, y es la vía
 * «manual por cliente» que pidió Cesar en el punto 19.
 *
 * `importe_usd` ES OBLIGATORIO Y ES EN DÓLARES, y no es un descuido del esquema:
 * todo el modelo financiero consolida al dólar (ver la pregunta 5 de Cesar, y la
 * cabecera de `PnlPage`). `moneda` dice en qué se emitió —es un dato del
 * documento— pero la cifra con la que se calcula es la de USD. Pedir el importe
 * en moneda local y convertirlo en la pantalla pondría el tipo de cambio en el
 * navegador, donde nadie puede auditarlo después.
 */
export interface FacturaManual {
  pais: string
  numero: string
  cliente: string
  fecha_emision: string
  importe_usd: number
  moneda?: string | null
  concepto?: string | null
  documento_fiscal?: string | null
  fecha_pago?: string | null
  estado?: string | null
}

export function crearFacturas(
  facturas: FacturaManual[],
  motivo: string,
): Promise<{ creadas: number; actualizadas: number; rechazadas?: unknown[] }> {
  return api.post("/finance/invoices", { facturas, motivo })
}

/**
 * UNA LÍNEA DEL P&L, ABIERTA POR PAÍS.
 *
 *   GET /api/finance/pnl/linea ?anio&account&sub_account
 *
 * El país es el único nivel que de verdad hay debajo de la línea: la hoja
 * guarda `(pais, bloque, account, sub_account, mes)` y nada más. Los clientes
 * viven en los contratos, y salen en «De dónde sale» — por eso la respuesta
 * trae `donde_estan_los_clientes`, para que la pantalla lo diga en vez de dejar
 * la pregunta colgando.
 */
export interface DetalleDeLinea {
  anio: number
  account: string
  sub_account: string
  moneda: string
  paises: { pais: string; meses: PorMes; total: number }[]
  total: number
  donde_estan_los_clientes: string
}

/**
 * `GET /finance/pnl/linea/quienes`: la línea abierta POR QUIÉN. Clientes
 * (facturas + contratos), proveedores (comprobantes) o personas (bruto del
 * maestro), mes a mes, más lo que no tiene línea asignada y la diferencia
 * contra la hoja. Ver `quienes.py`.
 */
export interface QuienDeLaLinea {
  /** `null` cuando no está en el maestro (una factura con un nombre que no coincide). */
  id: string | null
  nombre: string
  fuente: string
  meses: PorMes
  total: number
  en_maestro?: boolean
  regimen?: string | null
  /** Persona cuya línea salió del régimen y no de una elección. */
  por_defecto?: boolean
  /** Sólo en clientes que NO están en el maestro: lo que la factura sabe de ellos, para crearlos. */
  tax_id?: string | null
  pais?: string | null
  moneda?: string | null
}

export interface QuienesDeLaLinea {
  tipo: "cliente" | "proveedor" | "persona"
  anio: number
  account: string
  sub_account: string
  pais: string | null
  corte_mes: number | null
  filas: QuienDeLaLinea[]
  sin_linea: QuienDeLaLinea[]
  hoja: PorMes
  diferencia: PorMes
  lineas: string[]
  fuente: string
  sin_cotizacion?: number
}

export function fetchQuienesDeLaLinea(
  anio: number, account: string, subAccount: string, pais?: string,
): Promise<QuienesDeLaLinea> {
  return api.get<QuienesDeLaLinea>("/finance/pnl/linea/quienes", {
    query: { anio, account, sub_account: subAccount, pais: pais || undefined },
  })
}

/** Las líneas de una cuenta en la hoja, para el desplegable «Línea de la hoja» del maestro. */
export function fetchLineasDeLaHoja(account: string): Promise<{ account: string; lineas: string[] }> {
  return api.get<{ account: string; lineas: string[] }>("/finance/lineas", { query: { account } })
}

export function fetchDetalleDeLinea(
  anio: number, account: string, subAccount: string, pais?: string,
): Promise<DetalleDeLinea> {
  return api.get<DetalleDeLinea>("/finance/pnl/linea", {
    query: { anio, account, sub_account: subAccount, pais },
  })
}


/* ===========================================================================
 * QUÉ VA A FINANZAS — la frontera, las cuentas y el puente con la hoja
 *
 *   GET    /api/finance/ruteo              cuentas: va a Finanzas, quién manda, línea
 *   PUT    /api/finance/ruteo/cuenta       cambiar una cuenta (con motivo)
 *   PUT    /api/finance/ruteo/frontera     hasta qué mes manda la hoja (con motivo)
 *   DELETE /api/finance/ruteo/frontera     marcha atrás: la hoja vuelve a mandar
 *   GET    /api/finance/ruteo/puente       cada línea de la hoja y su cuenta
 *   PUT    /api/finance/ruteo/puente       decir a qué cuenta va una línea
 *   DELETE /api/finance/ruteo/puente       dejarla sin puente
 *   GET    /api/finance/ruteo/contraste    dónde no cuadran la hoja y el libro
 * ======================================================================== */

export async function fetchRuteo(pais?: string): Promise<RuteoRow> {
  return api.get<RuteoRow>("/finance/ruteo", { query: { pais: pais || undefined } })
}

export function enrutarCuenta(b: {
  codigo: string
  va_a_finanzas: boolean
  va_a_contabilidad: boolean
  linea_del_modelo: string | null
  fuente_finanzas: FuenteFinanzas
  motivo: string
}) {
  return api.put<{ codigo: string; nombre: string; aviso: string | null }>(
    "/finance/ruteo/cuenta", b)
}

export function declararFrontera(b: {
  anio: number; mes: number; pais: string | null; motivo: string
}) {
  return api.put<{ frontera: Frontera; avisos: string[]; siguiente: string }>(
    "/finance/ruteo/frontera", b)
}

export function quitarFrontera(motivo: string, pais?: string | null) {
  return api.delete<{ frontera: null; siguiente: string }>("/finance/ruteo/frontera", {
    query: { motivo, pais: pais || undefined },
  })
}

export async function fetchPuente(anio: number): Promise<PuenteRow> {
  return api.get<PuenteRow>("/finance/ruteo/puente", { query: { anio } })
}

export function tenderPuente(b: {
  account: string; sub_account: string; codigo_grupo: string; motivo?: string
}) {
  return api.put<{ codigo_grupo: string; cuenta: string }>("/finance/ruteo/puente", b)
}

export function quitarPuente(account: string, sub_account: string) {
  return api.delete<{ codigo_grupo: null }>("/finance/ruteo/puente", {
    query: { account, sub_account },
  })
}

export async function fetchContraste(anio: number, pais?: string): Promise<ContrasteRow> {
  return api.get<ContrasteRow>("/finance/ruteo/contraste", {
    query: { anio, pais: pais || undefined },
  })
}
